import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { useLocalization } from "@faire/web--source/common/localization/useLocalization";
import { logError } from "@faire/web--source/common/logging";
import { Button } from "@faire/web--source/slate/Button";
import { Checkbox } from "@faire/web--source/slate/Checkbox";
import { Input } from "@faire/web--source/slate/Input";
import { TextArea } from "@faire/web--source/slate/Input/TextArea";
import { Column, Flex, Grid } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { blogPage } from "@faire/retailer/paths";
import { MakerSignUpMobileWrapper } from "@faire/retailer/views/apply/MakerApplication/MakerSignUp/MakerSignUpMobileWrapper";
import { MakerSignUpWrapper } from "@faire/retailer/views/apply/MakerApplication/MakerSignUp/MakerSignUpWrapper";

import { BRAND_VALUES } from "./consts";
import { useMakerSignUpAdditionalQuestions } from "./useMakerSignUpAdditionalQuestions";

interface IProps {
  brandLeadToken: string;
}

const MakerSignUpAdditionalQuestionsBase: React.FC<IProps> = ({
  brandLeadToken,
}) => {
  const {
    intl: { locale },
  } = useLocalization();
  const { strictLocalize } = useStrictLocalization();
  const viewport = useViewport();

  const {
    handleYearEstablishedChanged,
    handleInstagramHandleChanged,
    isFaireValueSelected,
    onChangeFaireValue,
    additionalNotes,
    setAdditionalNotes,
    errors,
    onSave,
    formSubmitted,
    isPostingAdditionalInfo,
  } = useMakerSignUpAdditionalQuestions(brandLeadToken);

  const renderFormSubmittedContent = () => {
    return (
      <>
        <div>
          <Typography variant="displayMSerifRegular">
            <StrictLocalMsg
              defaultMessage="Thanks!"
              description={{
                text: "Message after user has submitted form providing more information about their brand.",
              }}
            />
          </Typography>
          <Spacer height="3x" />
          <Typography>
            <StrictLocalMsg
              defaultMessage="Your responses will be reviewed by someone on the Faire team.{lineBreak}{lineBreak}In the meantime, if you'd like to learn a little bit more about how brands use Faire for their wholesale business, you can visit our blog."
              values={DEPRECATED_backwardsCompatibleValues({
                lineBreak: <br />,
              })}
            />
          </Typography>
          <Spacer height="3x" />
        </div>
        <Button variant="primary" linkTo={`${blogPage(locale)}/thestudio/`}>
          <StrictLocalMsg
            defaultMessage="Go to Faire Blog"
            description={{ text: "CTA link to visit Faire blog" }}
          />
        </Button>
      </>
    );
  };

  if (!brandLeadToken) {
    logError("Missing brand lead token");
  }

  if (formSubmitted) {
    if (viewport.isMobile) {
      return (
        <MakerSignUpMobileWrapper>
          <ThankYouPageWrapper>
            {renderFormSubmittedContent()}
          </ThankYouPageWrapper>
        </MakerSignUpMobileWrapper>
      );
    }

    return (
      <MakerSignUpWrapper>
        <ThankYouPageWrapper>
          {renderFormSubmittedContent()}
        </ThankYouPageWrapper>
      </MakerSignUpWrapper>
    );
  }

  return (
    <MakerSignUpWrapper>
      <FormHeaderContentWrapper>
        <Typography variant="displaySSerifRegular">
          <StrictLocalMsg defaultMessage="Tell us more about your brand" />
        </Typography>
      </FormHeaderContentWrapper>
      <FormContentWrapper>
        <Spacer size="3x" />
        <DesktopRow>
          <Input
            label={strictLocalize({
              defaultMessage: "Year Established",
              description: { text: "Year that brand was established" },
            })}
            onChange={handleYearEstablishedChanged}
            error={errors.yearEstablished}
          />
          <Spacer size="2x" />
          <Input
            prefixNode="@"
            label={strictLocalize({
              defaultMessage: "Instagram Handle",
              description: { text: "Instagram handle of the brand" },
            })}
            onChange={handleInstagramHandleChanged}
            error={errors.instagramHandle}
          />
        </DesktopRow>

        <Spacer size="3x" />

        <Typography variant="paragraphSansMedium">
          <StrictLocalMsg defaultMessage="Which Faire values resonate most with your brand?" />
        </Typography>

        <Spacer size="2x" />

        <Grid columns={2} gap={16}>
          {BRAND_VALUES(strictLocalize).map((value, index) => (
            <Checkbox
              key={index}
              label={value.label}
              checked={isFaireValueSelected(value)}
              onChange={() => onChangeFaireValue(value)}
            />
          ))}
        </Grid>

        <Spacer size="3x" />

        <TextArea
          value={additionalNotes}
          label={strictLocalize({
            defaultMessage: "Anything else to share?",
          })}
          onChange={(e) => setAdditionalNotes(e.target.value)}
          style={{ height: "100px" }}
          maxLength={1000}
        />

        <Spacer size="3x" />
      </FormContentWrapper>
      <StickyContent>
        <CTAButton onClick={onSave} loading={isPostingAdditionalInfo}>
          <StrictLocalMsg
            defaultMessage="Submit"
            description={{ text: "CTA to submit a form" }}
          />
        </CTAButton>
        {errors.submission ? (
          <>
            <Spacer height="1x" />
            <Typography color={Core.text.critical}>
              {errors.submission}
            </Typography>
          </>
        ) : null}
      </StickyContent>
    </MakerSignUpWrapper>
  );
};

export const MakerSignUpAdditionalQuestions = observer(
  MakerSignUpAdditionalQuestionsBase
);

const ThankYouPageWrapper = styled(Column).attrs({ justify: "space-between" })`
  ${responsiveMediaQueries.tabletAndAbove} {
    min-height: 500px;
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x")};
  }
`;

const StickyContent = styled(Column)`
  padding: ${getSpacing(0, 0, "3x")};
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("2x")};
    background-color: ${Core.surface.inverse};
    width: 100%;
    position: sticky;
    z-index: 100;
    top: 0;
    bottom: 0;
  }
`;

const CTAButton = styled(Button).attrs({ variant: "primary" })`
  width: fit-content;
  min-width: 250px;
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const FormContentWrapper = styled(Column)`
  width: 100%;
  ${responsiveMediaQueries.mobile} {
    background-color: ${Core.surface.inverse};
    padding: 0 ${getSpacing("3x")};
  }
`;

const FormHeaderContentWrapper = styled(Column)`
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x")};
  }
`;

const DesktopRow = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})``;
